import React, { useMemo, useEffect, useRef } from "react";
import { ApolloProvider, useQuery } from "@apollo/client";
import PropTypes from "prop-types";
import { useParams, useLocation } from "react-router-dom";

import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";

import ViewportHelmet from "../components/ViewportHelmet";
import WebViewHeader, { addEntryIdAnchors } from "../components/WebViewHeader";
import PageContextProvider from "../PageContext";
import {
  resolveGlobals,
  resolveManual,
  resolveEntry,
} from "../craft/dataResolvers";
import {
  formatDate,
  debugLog,
  stripTooltipText,
  setPreview,
  scrollToAnchor,
} from "../utils";
import Paginator from "../components/Paginator";
import HeaderMax from "../components/HeaderMax";
import Header from "../components/Header";
import Cover from "../components/Cover";
import Manual from "../components/Manual";
import Contact from "../components/Contact";
import Footer from "../components/Footer";
import PreviewErrorBoundary from "../components/PreviewErrorBoundary";
import { createManualClient } from "../apollo/manualPreviewClient";
import { getPreviewParams, isPreviewParked, restorePreviewSession } from "../utils/previewParams";
// @ts-ignore
import { useAllManualsData } from "../craft/staticQueries";
import LIVE_MANUAL_PREVIEW_QUERY from "../craft/dynamicQueries/LiveManualPreviewQuery";

const ManualPreviewTemplate = ({
  manualId,
  siteId,
  manualPreviewClient,
  isProductionView = false, // default to non production view just for preview page
  isWebView = true,
  hasPreviewToken = false,
}) => {
  // --- set the flag BEFORE any resolvers are invoked ------------------
  setPreview(!isProductionView); // true when we’re in preview mode

  const allManuals = useAllManualsData();
  const containerRef = useRef(null);
  const tocCreatedRef = useRef(false);
  const { data, loading, error } = useQuery(LIVE_MANUAL_PREVIEW_QUERY, {
    variables: { id: manualId, siteId },
    client: manualPreviewClient,
  });

  // Detect if we should use paginator (not Safari, and browser supports it)
  // Decide once, *before* first paint, whether paginator can be used
  const shouldUsePaginator = useMemo(() => {
    if (typeof window === "undefined") return true; // during SSR
    const ua = navigator.userAgent;
    const isSafari = /Safari/.test(ua) && !/Chrome/.test(ua);
    const isChrome = /Chrome/.test(ua);
    if (isWebView) return false; // forced web-view
    return !(isSafari && !isChrome); // skip on Safari
  }, [isWebView]);

  // Effect to create TOC after content is rendered (for non-paginated preview)
  useEffect(() => {
    if (
      data &&
      containerRef.current &&
      !tocCreatedRef.current &&
      !shouldUsePaginator
    ) {
      // Small delay to ensure DOM is fully rendered
      const timer = setTimeout(() => {
        try {
          debugLog(
            "Creating integrated hamburger TOC menu for Safari/non-paginator mode"
          );
          // Import the modular hamburger menu functionality
          import("../utils/createToc")
            .then(({ createTocWithHamburgerMenu, ensureHeadingIds }) => {
              // Stamp chapterId-X / chapterId-X-topicId-Y IDs onto heading elements
              // so the WebViewHeader scroll-tracker can update breadcrumbs as the
              // user scrolls. Must run before hamburger menu reads the IDs.
              addEntryIdAnchors(containerRef.current, manual);

              // Find all heading anchors created by HeadingWrapper components
              const headingAnchors = containerRef.current.querySelectorAll(
                ".heading-anchor"
              );
              debugLog(`Found ${headingAnchors.length} heading anchors`);
              // Convert heading anchors to the format expected by createTocWithHamburgerMenu
              const processedHeadings = [];
              headingAnchors.forEach((anchor, index) => {
                const headingElement = anchor.querySelector(
                  ".title-chapter, .title-topic"
                );
                if (headingElement) {
                  const isChapter = headingElement.classList.contains(
                    "title-chapter"
                  );
                  const level = isChapter ? 1 : 2;

                  processedHeadings.push({
                    element: anchor, // Use the anchor element for TOC linking
                    id: anchor.id,
                    level,
                    text: stripTooltipText(headingElement).trim(),
                  });
                }
              });

              console.log(
                `Processed ${processedHeadings.length} headings for TOC`
              );
              // If we have direct headings, create the hamburger menu using the new modular approach
              if (processedHeadings.length > 0) {
                // Import hamburger menu functionality and create it
                import("../utils/hamburgerMenu").then(
                  ({ default: createHamburgerMenu }) => {
                    import("../utils/hamburgerMenuStyles").then(
                      ({ default: injectHamburgerMenuStyles }) => {
                        // Inject the hamburger menu styles
                        injectHamburgerMenuStyles();

                        // Create the hamburger menu directly
                        const hamburgerMenu = createHamburgerMenu({
                          headings: processedHeadings,
                          container: containerRef.current,
                          context: "preview",
                          manuals: allManuals,
                          currentManualId: manualId,
                          currentSiteId: siteId,
                        });
                        if (hamburgerMenu) {
                          console.log(
                            `Integrated hamburger TOC menu created with ${processedHeadings.length} headings using modular approach`
                          );
                          tocCreatedRef.current = true;
                          scrollToAnchor();
                        }
                      }
                    );
                  }
                );
              } else {
                // Fallback: try to create TOC from content using traditional selectors
                try {
                  const tocResult = createTocWithHamburgerMenu({
                    content: containerRef.current,
                    tocElement: "#fallback-toc-container", // We'll create this if needed
                    titleElements: [".title-chapter", ".title-topic"],
                    removeFallbackOnlyIfHeadingsFound: false,
                    container: containerRef.current,
                  });

                  if (tocResult && tocResult.hamburgerMenu) {
                    console.log(
                      `Hamburger menu created via fallback method with ${tocResult.headingCount} headings`
                    );
                    tocCreatedRef.current = true;
                    scrollToAnchor();
                  }
                } catch (fallbackError) {
                  console.error("Fallback TOC creation failed:", fallbackError);
                }
              }
            })
            .catch(error => {
              console.error(
                "Error importing modular TOC functionality:",
                error
              );
            });
        } catch (error) {
          console.error("Error creating integrated hamburger TOC menu:", error);
        }
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [data, shouldUsePaginator]);

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress size={64} />
      </Box>
    );
  }

  if (error) {
    console.error("Error fetching manual data:", error);
    console.error(
      "GraphQL Endpoint:",
      process.env.GATSBY_CRAFT_API_IMPORTEXPORT_URL
    );
    return (
      <PreviewErrorBoundary
        initialError={{ error }}
        initialMessage="Error loading manual."
      >
        {/* no children – boundary shows the fallback UI */}
      </PreviewErrorBoundary>
    );
  }

  debugLog("data", data);
  // Data is available
  const { uiText, contact } = resolveGlobals(data.globalSets);
  // If the entry is a chapter, create a dummy product so that `resolveManual` has the same structure:
  let manual = {};
  const { globalVariables } = data;
  if (data.entry && data.entry.typeHandle === "chapter") {
    const emptyProduct = {
      id: data.entry.id || manualId || 1,
      siteId: data.entry.siteId || siteId || "1",
      title: data.entry.title || data.entry.userTitle || "Chapter Preview",
      userTitle: data.entry.userTitle || "Chapter Preview",
      productIcon: null,
      productCategory: [],
      productFeatures: [],
      children: [data.entry],
      typeHandle: data.entry.typeHandle,
    };
    manual = resolveManual(emptyProduct, globalVariables);
  } else {
    manual = resolveManual(data.entry, globalVariables);
  }
  const varMap = manual.variableValues;
  const glossary = data.glossary.map((g, i) =>
    resolveEntry(g, i /* idx */, varMap)
  );
  const { productCategory, productFeatures } = manual;

  const initialState = {
    chrome: {
      title: manual?.userTitle || "Placeholder Title",
      htmlTitle: manual?.htmlTitle,
      logo: manual?.logo || "",
      color: manual?.color || "#000000",
      pageLayoutType: manual?.pageLayoutType || "letter",
    },
    uiText,
    contact,
    glossary,
    formattedDate: formatDate(new Date()),
    productCategory: productCategory || [],
    productFeatures: productFeatures || [],
    manualId: manual?.type === "product" && manual.id ? manual.id : null,
    isPreview: !isProductionView, // conditions don't run in preview mode
    isProductionView,
    isWebView,
  };

  if (
    !manual?.id ||
    (manual?.type !== "product" && manual?.type !== "chapter")
  ) {
    debugLog(
      "Missing manual.id or manual.type is not product nor chapter:",
      manual.id,
      manual.type
    );
    return (
      <PreviewErrorBoundary
        initialError={{ error }}
        initialMessage={`Error loading manual. Invalid ID ${manual?.id} or type ${manual?.type}`}
      >
        {/* no children – boundary shows the fallback UI */}
      </PreviewErrorBoundary>
    );
  }

  // pageLayoutType may be undefined, so default it to "letter"
  const pageLayoutType = manual?.pageLayoutType || "letter";
  let pageClass = "letter";
  switch (pageLayoutType) {
    case "QSG":
      pageClass = "QSG";
      break;
    // case "legal": pageClass = "legal"; break;
    default:
      pageClass = "letter";
      break;
  }

  const isQSG = pageLayoutType === "QSG";
  debugLog("pageLayoutType", pageLayoutType);
  debugLog("shouldUsePaginator", shouldUsePaginator);
  if (initialState.isPreview) {
    pageClass += " preview-mode ";
  }
  // Add non-paginated class if paginator is not being used
  if (!shouldUsePaginator) {
    pageClass += ` non-paginated-mode sm:w-full sm:max-w-full sm:mx-4 `; // viewport for mobile deviecvs
  }
  // Render different layouts based on whether paginator should be used
  if (!shouldUsePaginator) {
    return (
      <PageContextProvider initialState={initialState}>
        {isWebView && <ViewportHelmet />}
        <WebViewHeader
          manual={manual}
          containerRef={containerRef}
          defaultCollapsed
          isPreview={!isProductionView || hasPreviewToken}
          isProductionView={isProductionView}
          hasPreviewToken={hasPreviewToken}
        />
        <div className="non-paginated-background">
          <div className="non-paginated-content-wrapper">
            <div ref={containerRef} className={pageClass}>
              <Header className="Header--first" onFirstPage />
              <div className="cover-component">
                <Cover
                  chapters={manual.chapters}
                  shouldUsePaginator={shouldUsePaginator}
                />
              </div>
              <Manual manual={manual} />
              <Contact />
              <Footer className="Footer--first" onFirstPage />
              <div id="preview" />
            </div>
          </div>
        </div>
      </PageContextProvider>
    );
  }

  // GOTCHA: <Footer /> must go above <Manual /> else it will not show on all pages
  return (
    <PageContextProvider initialState={initialState}>
      <div ref={containerRef} className={pageClass}>
        {isQSG ? (
          <Paginator disableToc>
            <HeaderMax onFirstPage />
            <Footer onFirstPage />
            <Manual manual={manual} />
            <Footer />
          </Paginator>
        ) : (
          <Paginator>
            <Header onFirstPage />
            <Footer onFirstPage />
            <Cover chapters={manual.chapters} />
            <Header />
            <Footer />
            {/* The TOC container will be created by the Paginator's createPaginatedToc */}
            <div
              id="my-toc-content"
              className="page-break toc-container ml-page-margin max-w-contentmaxmargin"
            >
              {/* TOC will be populated by createPaginatedToc() in Paginator */}
            </div>
            <Manual manual={manual} />
            <Contact />
          </Paginator>
        )}
        <div id="preview" />
      </div>
    </PageContextProvider>
  );
};

ManualPreviewTemplate.propTypes = {
  manualId: PropTypes.string.isRequired,
  siteId: PropTypes.string.isRequired,
  manualPreviewClient: PropTypes.object.isRequired,
  isProductionView: PropTypes.bool,
  isWebView: PropTypes.bool,
  hasPreviewToken: PropTypes.bool,
};

//
// Wrap Template with ApolloProvider
//
const ManualPreviewTemplateWithApollo = () => {
  // Extract route parameters
  const { manualId, siteId } = useParams();
  debugLog("params", { manualId, siteId });

  // Extract query parameters from the URL
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);

  // Read preview params — URL takes priority, sessionStorage fills in the
  // gaps when the user navigates to a page that doesn't carry the params
  // (e.g. Home or Switch Manual links). getPreviewParams() also writes to
  // sessionStorage whenever URL params are present, keeping the store fresh.
  const storedPreviewParams = getPreviewParams();

  // Pull out all the same params as the Topic preview does
  const token = urlParams.get("token") ?? storedPreviewParams['token'] ?? null;
  const xCraftPreview = urlParams.get("x-craft-preview") ?? storedPreviewParams['x-craft-preview'] ?? null;
  const xCraftLivePreview = urlParams.get("x-craft-live-preview") ?? storedPreviewParams['x-craft-live-preview'] ?? null;
  const isProductionViewDisabled =
    urlParams.get("live-production") === "0" ||
    urlParams.get("production") === "0" ||
    false;
  const isProductionView =
    urlParams.get("production") === "1" || // if production view explictly set to on, set it even if xCraftLivePreview is not set
    (!isProductionViewDisabled && !xCraftLivePreview); // production view not explictly disabled and we are not previewing from craft
  const isWebViewDisabled =
    urlParams.get("web") === "0" || urlParams.get("web-view") === "0" || false;
  const isWebView = !isWebViewDisabled;

  // If fresh live preview params arrive via URL, auto-restore a parked session
  const hasParkedSession = isPreviewParked();
  if (xCraftLivePreview && hasParkedSession) {
    restorePreviewSession();
  }
  // Show preview header if we have a live preview token (active or parked)
  const hasPreviewToken = !!(token || xCraftLivePreview || hasParkedSession);

  // Safely decode api-url so we do not feed "null" to decodeURIComponent
  const rawApiUrl = urlParams.get("api-url") ?? storedPreviewParams['api-url'] ?? null;
  const apiUrl =
    rawApiUrl && rawApiUrl.toLowerCase() !== "null"
      ? decodeURIComponent(rawApiUrl)
      : null;

  // Create or memoize the client
  const client = useMemo(() => {
    return createManualClient(token, apiUrl);
  }, [token, xCraftPreview, xCraftLivePreview, apiUrl]);

  // Security: Ensure route parameters are present
  if (!manualId || !siteId) {
    return (
      <PreviewErrorBoundary
        initialMessage={`Error loading manual. Invalid ID ${manualId} or site ${siteId}`}
      />
    );
  }

  return (
    <ApolloProvider client={client}>
      <PreviewErrorBoundary>
        <ManualPreviewTemplate
          manualId={manualId}
          siteId={siteId}
          manualPreviewClient={client}
          isProductionView={isProductionView}
          isWebView={isWebView}
          hasPreviewToken={hasPreviewToken}
        />
      </PreviewErrorBoundary>
    </ApolloProvider>
  );
};

export default ManualPreviewTemplateWithApollo;
